import { Injectable } from '@angular/core';
import { ComponentStore, OnStoreInit, tapResponse } from '@ngrx/component-store';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
  FileTypesEnum,
  PdSellerAgreementData,
  SellerService,
  SellerUpdate,
  SellerValidation,
  VerificationStatusEnum,
  VerificationTypeEnum,
} from '../../http';
import { of, withLatestFrom } from 'rxjs';

export interface PdBusinessData {
  companyName: string | null;
  businessNumber: number | null;
  addressCity: string | null;
  addressStreet: string | null;
  addressNumber: string | null;
  vendorNumber: string | null;
  phone: string | null;
}

export interface PdBankData {
  website: string | null;
  businessType: number | null;
  bankCode: number | null;
  bankBranch: string | null;
  bankAccount: string | null;
}

export interface Base64FileData {
  base64: string;
  name: string;
  mimeType: string;
}

export interface PdDocumentsData {
  signature: string;
  corporateCertificate?: Base64FileData;
  accountOwnerId?: Base64FileData;
  bankAccountOwnership?: Base64FileData;
  businessStamp: Base64FileData;
}

export interface FilesVisibility {
  corporateCertificate: boolean;
  accountOwnerId: boolean;
  bankAccountOwnership: boolean;
  businessStamp: boolean;
}

export enum PdCollectDataSteps {
  GoBack,
  BusinessData,
  BankData,
  Documents,
  Success,
  Proceed,
}

const pdCollectDataSteps = [
  PdCollectDataSteps.GoBack,
  PdCollectDataSteps.BusinessData,
  PdCollectDataSteps.BankData,
  PdCollectDataSteps.Documents,
  PdCollectDataSteps.Success,
];

export interface PdCollectDataState {
  currentStep: PdCollectDataSteps;
  sellerHash: string;
  shownFiles: FilesVisibility;
  data: {
    businessData?: PdBusinessData;
    bankData?: PdBankData;
    documentsData?: PdDocumentsData;
  };
}

@Injectable()
export class PdCollectDataStore extends ComponentStore<PdCollectDataState> implements OnStoreInit {
  public currentStep$ = this.select((state) => state.currentStep);
  public businessData$ = this.select((state) => state.data?.businessData);
  public bankData$ = this.select((state) => state.data?.bankData);
  public filesVisibility$ = this.select((state) => state.shownFiles);

  constructor(private readonly sellerService: SellerService) {
    super();
  }

  public ngrxOnStoreInit(): void {
    this.getVisibility();
  }

  public submitBusinessData = this.effect<PdBusinessData>((data$) =>
    data$.pipe(
      withLatestFrom(this.select((state) => state.sellerHash)),
      switchMap(([data, hash]) =>
        this.sellerService.updateSeller(hash, PdCollectDataStore.createUpdateDataForBusinessData(data)).pipe(
          map(() => data),
          catchError(() => of(data)),
        ),
      ),
      tap((data) => this.setBusinessData(data)),
    ),
  );

  public submitBankData = this.effect<PdBankData>((data$) =>
    data$.pipe(
      withLatestFrom(this.select((state) => state.sellerHash)),
      switchMap(([data, hash]) =>
        this.sellerService.updateSeller(hash, PdCollectDataStore.createUpdateDataForBankData(data)).pipe(
          map(() => data),
          catchError(() => of(data)),
        ),
      ),
      tap((data) => this.setBankData(data)),
    ),
  );

  public submitDocuments = this.effect<PdDocumentsData>((data$) =>
    data$.pipe(
      withLatestFrom(this.select((state) => state)),
      switchMap(([data, state]) => {
        const files = [];
        if (state.shownFiles.accountOwnerId && data.accountOwnerId) {
          files.push({
            name: data.accountOwnerId.name,
            type: FileTypesEnum.SocialId,
            base64: data.accountOwnerId.base64,
            mime_type: data.accountOwnerId.mimeType,
          });
        }
        if (state.shownFiles.bankAccountOwnership && data.bankAccountOwnership) {
          files.push({
            name: data.bankAccountOwnership.name,
            type: FileTypesEnum.BankAccountOwnership,
            base64: data.bankAccountOwnership.base64,
            mime_type: data.bankAccountOwnership.mimeType,
          });
        }
        if (state.shownFiles.corporateCertificate && data.corporateCertificate) {
          files.push({
            name: data.corporateCertificate.name,
            type: FileTypesEnum.CorporateCertificate,
            base64: data.corporateCertificate.base64,
            mime_type: data.corporateCertificate.mimeType,
          });
        }
        if (files.length > 0) {
          return this.sellerService.sendSellerFiles(state.sellerHash, files).pipe(
            map(() => [data, state] as const),
            catchError(() => of([data, state] as const)),
          );
        }
        return of([data, state] as const);
      }),
      switchMap(([data, state]) =>
        this.sellerService.createPdSellerAgreement(
          state.sellerHash,
          PdCollectDataStore.createDataForPdAgreement(data, state.data?.businessData, state.data.bankData),
        ),
      ),
      tapResponse(
        () => this.patchState({ currentStep: PdCollectDataSteps.Success }),
        // TODO: handle error in a better way
        (error) => console.log(error),
      ),
    ),
  );

  public proceed = this.effect<void>((data$) => data$.pipe(tap(() => this.goToHomePage())));

  private goToHomePage = this.updater<void>((state, data) => ({
    ...state,
    currentStep: PdCollectDataSteps.Proceed,
  }));

  private setBusinessData = this.updater<PdBusinessData>((state, data) => ({
    ...state,
    data: {
      ...state.data,
      businessData: data,
    },
    currentStep: PdCollectDataSteps.BankData,
  }));

  private setBankData = this.updater<PdBankData>((state, data) => ({
    ...state,
    data: {
      ...state.data,
      bankData: data,
    },
    currentStep: PdCollectDataSteps.Documents,
  }));

  public prevStep = this.updater((state) => {
    const prevIndex = pdCollectDataSteps.indexOf(state.currentStep) - 1;
    return {
      ...state,
      currentStep: pdCollectDataSteps[prevIndex >= 0 ? prevIndex : 0],
    };
  });

  private static createUpdateDataForBusinessData(data: PdBusinessData): SellerUpdate {
    return {
      seller_merchant_name: data.companyName || undefined,
      seller_inc_code: data.businessNumber || undefined,
      seller_address_city: data.addressCity || undefined,
      seller_address_street: data.addressStreet || undefined,
      seller_address_street_number: data.addressNumber || undefined,
      seller_phone: data.phone || undefined,
    };
  }

  private static createUpdateDataForBankData = (data: PdBankData): SellerUpdate => ({
    seller_site_url: data.website || undefined,
    seller_bank_code: data.bankCode || undefined,
    seller_bank_branch: data.bankBranch || undefined,
    seller_bank_account_number: data.bankAccount || undefined,
    seller_person_business_type: data.businessType || undefined,
  });

  private static createDataForPdAgreement(documents: PdDocumentsData, business?: PdBusinessData, bank?: PdBankData): PdSellerAgreementData {
    return {
      seller_merchant_name: business?.companyName || '',
      seller_inc_code: business?.businessNumber?.toString() || '',
      seller_address: `${business?.addressCity || ''} ${business?.addressStreet || ''} ${business?.addressNumber || ''}`,
      seller_person_business_type: bank?.businessType?.toString() || '',
      seller_phone: business?.phone || '',
      seller_bank_code: bank?.bankCode?.toString() || '',
      seller_bank_branch: bank?.bankBranch || '',
      seller_bank_account_number: bank?.bankAccount || '',
      fields: {
        signature: { base64: documents.signature },
        business_stamp: { base64: documents.businessStamp.base64 },
        business_stamp2: { base64: documents.businessStamp.base64 },
        signature2: { base64: documents.signature },
      },
    };
  }

  private getVisibility = this.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.select((state) => state.sellerHash)),
      switchMap(([_, hash]) => this.sellerService.getSellerValidations(hash)),
      map((res) => this.transformValidationsToVisibility(res.items)),
      tapResponse(
        (validations) => this.patchState({ shownFiles: validations }),
        (error) => console.log(error),
      ),
    ),
  );

  private transformValidationsToVisibility(validations: Array<SellerValidation>): FilesVisibility {
    return validations.reduce<FilesVisibility>(
      (acc, item) => {
        switch (item.type) {
          case VerificationTypeEnum.BankAccountOwnership:
            acc.bankAccountOwnership = item.status === VerificationStatusEnum.Rejected;
            break;
          case VerificationTypeEnum.CorporateCertificate:
            acc.corporateCertificate = item.status === VerificationStatusEnum.Rejected;
            break;
          case VerificationTypeEnum.SocialId:
            acc.accountOwnerId = item.status === VerificationStatusEnum.Rejected;
            break;
        }
        return acc;
      },
      {
        corporateCertificate: true,
        accountOwnerId: true,
        bankAccountOwnership: true,
        businessStamp: true,
      },
    );
  }
}
